import apiClient from './api-client';

export interface BenchmarkRate {
  benchmark_type: string;
  tenor?: string | null;
  rate: number;
  date?: string;
  source?: string;
  updated_by?: string;
  created_at?: string;
  updated_at?: string;
}

export interface BenchmarkRateDetail {
  id: number;
  benchmark_date: string;
  benchmark_type: string;
  term_years: number;
  rate: number;
  source?: string;
  created_at: string;
}

export interface BenchmarkRateCreate {
  benchmark_date: string;
  benchmark_type: string;
  term_years: number;
  rate: number;
  source?: string;
}

export interface BenchmarkRateUpdate {
  effective_date: string;
  rates: BenchmarkRate[];
  notes?: string;
}

export interface BenchmarkBulkUploadRequest {
  benchmark_type: string;
  file: string;
  sheet_name?: string;
  start_row?: number;
}

export interface BenchmarkBulkUploadResponse {
  message: string;
  total_records: number;
  records_created: number;
  records_updated: number;
  validation_errors: string[];
  warnings: string[];
}

export const benchmarkRatesService = {
  // Get current benchmark rates
  async getCurrentBenchmarks(): Promise<BenchmarkRate[]> {
    const response = await apiClient.get(
      '/api/v1/portfolios/benchmarks/current'
    );
    return response.data;
  },

  // Get benchmark rates view
  async getBenchmarkRatesView(): Promise<unknown[]> {
    const response = await apiClient.get(
      '/api/v1/portfolios/benchmark-rates-view'
    );
    return response.data;
  },

  // Create a new benchmark rate
  async createBenchmark(
    benchmark: BenchmarkRateCreate
  ): Promise<BenchmarkRateDetail> {
    const response = await apiClient.post(
      '/api/v1/portfolios/benchmarks',
      benchmark
    );
    return response.data;
  },

  // Update benchmark rates
  async updateBenchmarks(
    update: BenchmarkRateUpdate
  ): Promise<{ message: string }> {
    const response = await apiClient.put(
      '/api/v1/portfolios/benchmarks',
      update
    );
    return response.data;
  },

  // Get a specific benchmark rate by ID
  async getBenchmarkById(benchmarkId: number): Promise<BenchmarkRateDetail> {
    const response = await apiClient.get(
      `/api/v1/portfolios/benchmarks/${benchmarkId}`
    );
    return response.data;
  },

  // Delete a benchmark rate
  async deleteBenchmark(benchmarkId: number): Promise<{ message: string }> {
    const response = await apiClient.delete(
      `/api/v1/portfolios/benchmarks/${benchmarkId}`
    );
    return response.data;
  },

  // Bulk upload benchmark rates
  async bulkUploadBenchmarks(
    request: BenchmarkBulkUploadRequest
  ): Promise<BenchmarkBulkUploadResponse> {
    const response = await apiClient.post(
      '/api/v1/portfolios/benchmarks/bulk-upload',
      request
    );
    return response.data;
  },

  // Get loan benchmark data
  async getLoanBenchmark(): Promise<unknown[]> {
    const response = await apiClient.get('/api/v1/valuation/loan-benchmark');
    return response.data;
  },

  // Get current benchmark from valuation
  async getBenchmarkCurrent(): Promise<unknown[]> {
    const response = await apiClient.get('/api/v1/valuation/benchmark-current');
    return response.data;
  },
};
